// Typed fetch client for the CUSTOMER-facing surfaces (Help Center page + widget-parity
// React side). Same-origin `/api/*` (Vite proxies to the Worker in dev; served together
// in prod). Carries the local identity override (?as=customer) so the Worker scopes to the
// demo location in dev. Shapes follow PLAN.md "Phase 2–5 endpoint contract".
//
// NOTE: the vanilla Shadow-DOM widget (src/widget/**) does NOT import this module — it stays
// dependency-free and talks to a configurable `api-base`. This client is for React only.
import type {
  PlanContext, Ticket, TicketAttachment, TicketComment, TicketContext, TicketMeta,
} from './suite-types';
import type { ContentBundle, DocItem, TourItem, VideoItem } from './types';
import { authHeaders, exchangeSession } from './session';

// ── low-level helpers ──────────────────────────────────────────────
// The resolved customer identity (from /api/me — SSO in prod, ?as override in dev). Every call
// must carry it: the Worker's resolveAgencyId reads agencyId (NOT ?as) and now FAILS LOUD (400)
// when it's absent — real tickets once silently scattered under `demo-agency` without this.
// Mirrors agencyApi's setActiveAgencyId fix; CustomerView sets it from `me` before fetching.
let activeScope: { agencyId: string | null; locationId: string | null; userId: string | null } = {
  agencyId: null, locationId: null, userId: null,
};
export function setCustomerScope(s: { agencyId?: string | null; locationId?: string | null; userId?: string | null }): void {
  activeScope = { agencyId: s.agencyId ?? null, locationId: s.locationId ?? null, userId: s.userId ?? null };
}

function scopeParams(): string {
  const parts: string[] = [];
  if (typeof window !== 'undefined') {
    const as = new URLSearchParams(window.location.search).get('as');
    if (as) parts.push(`as=${encodeURIComponent(as)}`);
  }
  if (activeScope.agencyId) parts.push(`agencyId=${encodeURIComponent(activeScope.agencyId)}`);
  if (activeScope.locationId) parts.push(`locationId=${encodeURIComponent(activeScope.locationId)}`);
  if (activeScope.userId) parts.push(`userId=${encodeURIComponent(activeScope.userId)}`);
  return parts.join('&');
}

function withAs(path: string): string {
  const sp = scopeParams();
  if (!sp) return `/api${path}`;
  return `/api${path}${path.includes('?') ? '&' : '?'}${sp}`;
}

async function req<T>(method: string, path: string, body?: unknown, _retried = false): Promise<T> {
  const res = await fetch(withAs(path), {
    method,
    headers: {
      accept: 'application/json',
      ...authHeaders(),
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401 && !_retried) {
    await exchangeSession();
    return req<T>(method, path, body, true);
  }
  if (!res.ok) throw new Error(`${method} ${path} → ${res.status}`);
  return res.json() as Promise<T>;
}

const get = <T>(path: string) => req<T>('GET', path);
const post = <T>(path: string, body?: unknown) => req<T>('POST', path, body);

// Some Worker stubs wrap payloads inconsistently while parallel work is in flight.
// Tolerate `{tickets:[…]}` | `[…]`.
function arr<T>(v: unknown, key: string): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v && typeof v === 'object' && Array.isArray((v as Record<string, unknown>)[key])) {
    return (v as Record<string, T[]>)[key];
  }
  return [];
}

// ── Plan-lock overlay on content items ─────────────────────────────
// The content bridge now augments each doc/video/tour with plan-lock info. We can't edit
// the shared `DocItem`/`VideoItem`/`TourItem` (owned elsewhere), so we intersect locally —
// this ADDS fields, it does not redefine the base shapes.
export interface PlanLock {
  locked?: boolean;
  requiresPlan?: { id: string; name: string } | null;
}
export type LockedDoc = DocItem & PlanLock;
export type LockedVideo = VideoItem & PlanLock;
export type LockedTour = TourItem & PlanLock;
export interface LockedBundle {
  area: string | null;
  areaLabel: string | null;
  docs: LockedDoc[];
  videos: LockedVideo[];
  tours: LockedTour[];
}

function asLockedBundle(b: ContentBundle): LockedBundle {
  // The base ContentBundle typing lacks lock fields, but the Worker sends them through —
  // widen without inventing data.
  return b as unknown as LockedBundle;
}

// ── Ticket contract shapes ─────────────────────────────────────────
export interface TicketsMetaResponse {
  statuses: TicketMeta[];
  priorities: TicketMeta[];
  categories: TicketMeta[];
}
export interface TicketDetail {
  ticket: Ticket | null;
  comments: TicketComment[];
}
export interface NewTicketInput {
  subject: string;
  body: string;
  category_id?: string | null;
  context_area?: string | null;
  context_route?: string | null;
  // Rich browser/screen/route context captured at submit time (widget UX v2).
  context_meta?: TicketContext | null;
  requester_name?: string | null;
  requester_email?: string | null;
  requester_user_id?: string | null;
  location_id?: string | null;
}
// Attachment staged client-side then uploaded to POST /tickets/:id/attachments.
export interface NewAttachmentInput {
  kind: 'screenshot' | 'file';
  filename: string;
  contentType: string;
  dataBase64: string; // raw base64 (no data: URL prefix)
}
export interface NewCommentInput {
  body: string;
  author_type: 'agent' | 'customer' | 'system';
  author_name?: string | null;
  is_internal?: boolean;
}

// Public widget config (GET /api/settings/widget) — a standalone bootstrap shape (NOT the full
// AgencySettings): agency theme/upgrade/position/brand plus the 5 per-feature toggles the widget
// gates its UI by. Toggles are optional; absent ⇒ treat as enabled.
export interface WidgetSettings {
  agency_id?: string;
  widget_enabled?: boolean;
  widget_position?: string;
  widget_theme?: 'dark' | 'light';
  upgrade_url?: string | null;
  widget_docs_enabled?: boolean;
  widget_videos_enabled?: boolean;
  widget_tours_enabled?: boolean;
  widget_tickets_enabled?: boolean;
  widget_plans_enabled?: boolean;
  accent?: string | null;
  app_name?: string | null;
  logo_url?: string | null;
}

// Analytics event payload — matches worker/routes/events.ts (agencyId/locationId/userId camelCase).
export interface EventInput {
  agencyId?: string | null;
  locationId?: string | null;
  userId?: string | null;
  event: string;
  area?: string | null;
  meta?: unknown;
}

export const customerApi = {
  // ── Content (Phase 1 + plan-lock overlay from Phase 2) ──
  async library(agencyId: string | null): Promise<LockedBundle> {
    const b = await get<ContentBundle>(
      `/content/library${agencyId ? `?agencyId=${encodeURIComponent(agencyId)}` : ''}`,
    );
    return asLockedBundle(b);
  },
  async context(route: string, agencyId: string | null): Promise<LockedBundle> {
    const b = await get<ContentBundle>(
      `/content/context?route=${encodeURIComponent(route)}${
        agencyId ? `&agencyId=${encodeURIComponent(agencyId)}` : ''
      }`,
    );
    return asLockedBundle(b);
  },

  // ── Plans (P2) — already offered/group filtered by the server ──
  planContext: (locationId: string) =>
    get<PlanContext>(`/plans?locationId=${encodeURIComponent(locationId)}`),

  // ── Help desk (P3) ──
  ticketsMeta: () => get<TicketsMetaResponse>('/tickets/meta').catch(
    (): TicketsMetaResponse => ({ statuses: [], priorities: [], categories: [] }),
  ),
  async myTickets(): Promise<Ticket[]> {
    return arr<Ticket>(await get<unknown>('/tickets?mine=1'), 'tickets');
  },
  async ticket(id: string): Promise<TicketDetail> {
    const v = await get<{ ticket?: Ticket | null; comments?: TicketComment[] }>(
      `/tickets/${encodeURIComponent(id)}`,
    );
    return { ticket: v.ticket ?? null, comments: v.comments ?? [] };
  },
  // Create a ticket, optionally carrying rich captured context (context_meta).
  createTicket: (input: NewTicketInput) =>
    post<{ ok?: boolean; id?: string; ticket?: Ticket }>('/tickets', input),
  // Upload one staged attachment (screenshot/file) to a created ticket.
  uploadAttachment: (id: string, input: NewAttachmentInput) =>
    post<{ ok?: boolean; attachment?: TicketAttachment }>(
      `/tickets/${encodeURIComponent(id)}/attachments`,
      input,
    ),
  addComment: (id: string, input: NewCommentInput) =>
    post<{ ok?: boolean; comment?: TicketComment }>(
      `/tickets/${encodeURIComponent(id)}/comments`,
      input,
    ),

  // ── Settings / theme (P5) — the PUBLIC widget config: widget_theme + upgrade_url +
  // per-feature toggles the widget gates its UI by. ──
  settings: (agencyId?: string | null) =>
    get<WidgetSettings>(`/settings/widget${agencyId ? `?agencyId=${encodeURIComponent(agencyId)}` : ''}`),

  // ── Analytics (P4) — best-effort, never throws ──
  event: (payload: EventInput) =>
    post<{ ok: boolean }>('/events', payload).catch(() => ({ ok: false })),
  // Convenience: report elapsed watch time for a video (fired on stop/close).
  videoWatch: (contentId: string, title: string, seconds: number, extra?: Partial<EventInput>) =>
    post<{ ok: boolean }>('/events', {
      ...extra, event: 'video_watch', meta: { contentId, title, seconds },
    }).catch(() => ({ ok: false })),
};
